// NOTICE: KAPTION-INTEGRATION
// Shared composable for Dashboard App SDK communication
// Used by both Frame.vue (Dashboard Apps) and KaptionCopilotPanel.vue (Copilot)
// This ensures feature parity and eliminates code duplication
//
// Exports:
// - useDashboardAppSDK: Full composable for Composition API
// - DashboardAppSDKUtils: Standalone utilities for Options API
// - SDK_MESSAGE_TYPES: Constants for message types

import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// ============================================
// CONSTANTS
// ============================================

export const SDK_MESSAGE_TYPES = {
  // Inbound (SDK → Container)
  SDK_READY: 'chatwoot-dashboard-app-sdk-ready',
  SDK_EVENT: 'chatwoot-dashboard-app-sdk',
  LEGACY_FETCH_INFO: 'chatwoot-dashboard-app:fetch-info',

  // Outbound (Container → SDK)
  CONTEXT: 'chatwoot-dashboard-context',
  SETTINGS_UPDATE: 'chatwoot-dashboard-settings-update',
  UI_CONFIG: 'chatwoot-dashboard-ui-config',
  CONTEXT_REQUEST: 'chatwoot-dashboard-context-request',
  EVENT_RESULT: 'chatwoot-dashboard-event-result',
  TOKEN_REFRESHED: 'chatwoot-dashboard-token-refreshed',

  // Inter-App Tool & Context Protocol (Container → SDK)
  TOOL_INVOKE: 'chatwoot-dashboard-tool-invoke',
  TOOL_CANCEL: 'chatwoot-dashboard-tool-cancel',
  CTX_FETCH: 'chatwoot-dashboard-ctx-fetch',
  CATALOG_UPDATED: 'chatwoot-dashboard-catalog-updated',
  TOOL_CATALOG: 'chatwoot-dashboard-tool-catalog',
  CTX_CATALOG: 'chatwoot-dashboard-ctx-catalog',
  EVENT_PARTIAL: 'chatwoot-dashboard-event-partial',

  // Note Card Actions (Container → SDK)
  NOTE_CARD_ACTION: 'chatwoot-dashboard-note-card-action',

  // Legacy outbound
  LEGACY_APP_CONTEXT: 'appContext',
};

export const SDK_EVENT_TYPES = {
  SEND_MESSAGE: 'send-message',
  CONTEXT_REFRESH: 'context-refresh-request',
  LOG: 'log',
  COPY_TO_COMPOSER: 'copy-to-composer',
  CONTEXT_RESPONSE: 'context-response',
  TOKEN_REFRESH: 'token-refresh',
  OPEN_CONVERSATION: 'open-conversation',

  // Inter-App Tool & Context Protocol (SDK → Container)
  TOOL_REGISTER: 'tool-register',
  TOOL_UNREGISTER: 'tool-unregister',
  CTX_REGISTER: 'ctx-register',
  CTX_UNREGISTER: 'ctx-unregister',
  TOOL_DISCOVER: 'tool-discover',
  CTX_DISCOVER: 'ctx-discover',
  TOOL_INVOKE: 'tool-invoke',
  TOOL_RESULT: 'tool-result',
  TOOL_PARTIAL: 'tool-partial',
  TOOL_ERROR: 'tool-error',
  TOOL_CANCEL: 'tool-cancel',
  CTX_FETCH: 'ctx-fetch',
  CTX_RESULT: 'ctx-result',
  CTX_ERROR: 'ctx-error',
};

// ============================================
// STANDALONE UTILITIES (for Options API)
// ============================================

/**
 * Utility functions that can be used directly in Options API components
 * These are stateless and don't require Vue reactivity
 */
export const DashboardAppSDKUtils = {
  /**
   * Generate a unique request ID for tracking responses
   */
  generateRequestId() {
    return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Decode base64 attachments to File objects
   */
  decodeAttachments(attachments) {
    if (!attachments || attachments.length === 0) return [];

    return attachments.map(att => {
      const byteString = atob(att.data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: att.mimeType });
      return new File([blob], att.title || 'attachment', {
        type: att.mimeType,
      });
    });
  },

  /**
   * Post a message to an iframe
   */
  postMessageToIframe(iframe, message, logPrefix = '[SDK]') {
    if (!iframe || !iframe.contentWindow) return false;
    try {
      // NOTICE: KAPTION-INTEGRATION - Restrict postMessage to the app's origin
      // instead of using '*' which allows any window to receive the message
      let targetOrigin = '*';
      try {
        if (iframe.src) {
          targetOrigin = new URL(iframe.src).origin;
        }
      } catch {
        // Invalid URL — fall back to '*'
      }
      iframe.contentWindow.postMessage(JSON.stringify(message), targetOrigin);
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to post message:`, error);
      return false;
    }
  },

  /**
   * Build context message
   */
  buildContextMessage(context, surface = 'main', sidebarParams = {}) {
    return {
      type: SDK_MESSAGE_TYPES.CONTEXT,
      data: {
        conversation: context.conversation,
        contact: context.contact,
        currentAgent: context.currentAgent,
        ui: { surface, sidebarParams },
        timestamp: Date.now(),
      },
    };
  },

  /**
   * Build settings update message
   */
  buildSettingsMessage(
    theme,
    language,
    timezone,
    surface = 'main',
    sidebarParams = {}
  ) {
    return {
      type: SDK_MESSAGE_TYPES.SETTINGS_UPDATE,
      data: {
        userPreferences: { theme, language, timezone },
        ui: { surface, sidebarParams },
      },
    };
  },

  /**
   * Build UI config message
   */
  buildUIConfigMessage(config = {}) {
    return {
      type: SDK_MESSAGE_TYPES.UI_CONFIG,
      data: {
        keyboard: config.keyboard || { enabled: true, type: 'text' },
        display: config.display || { mode: 'full', scrollable: true },
        capabilities: config.capabilities || {
          canSendMessages: true,
          canSendAttachments: true,
          canAccessContacts: true,
        },
        timestamp: Date.now(),
      },
    };
  },

  /**
   * Build event result message
   */
  buildEventResultMessage(requestId, success, sdkType, data = {}) {
    return {
      type: SDK_MESSAGE_TYPES.EVENT_RESULT,
      data: {
        requestId,
        success,
        sdkType,
        ...data,
      },
    };
  },

  // NOTICE: KAPTION-INTEGRATION
  // Shared openConversation handler — invoked by BOTH Frame.vue (sidebar /
  // main surface) and routes/dashboard/fullscreen-app/Index.vue (full
  // surface). Keeping the logic here avoids drift between surfaces and
  // means the contract is enforced from one place.
  //
  // Dependencies are passed in so this works in both Options API and
  // Composition API contexts. Returns nothing — the result is delivered
  // through ackResult(success, dataOrError).
  //
  // @param {Object} payload - { requestId, conversationId, inboxId, target, closeFullScreen }
  // @param {Object} ctx
  // @param {string|number} ctx.accountId       — live account id from Vuex
  // @param {Object}        ctx.store           — Vuex store for getConversationById
  // @param {Object}        ctx.router          — Vue Router instance for push
  // @param {string|undefined} ctx.currentRouteConversationId — to detect alreadyActive
  // @param {'main'|'sidebar'|'full'} ctx.surface
  // @param {Function}      ctx.conversationApiShow  — async (id) => { data: conv }
  // @param {Function}      ctx.buildConversationPath — (accountId, activeInbox, id) => path
  // @param {Function}      ctx.ackResult       — (success, sdkType, dataOrError) => void
  // @param {Function}      [ctx.onFrameClosed] — () => void, called on full+closeFullScreen
  async handleOpenConversation(payload, ctx) {
    const { requestId, conversationId, inboxId, target, closeFullScreen } =
      payload || {};
    const sdkType = SDK_EVENT_TYPES.OPEN_CONVERSATION;

    if (!Number.isInteger(conversationId) || conversationId <= 0) {
      ctx.ackResult(false, sdkType, requestId, {
        error: {
          code: 'INVALID_CONVERSATION_ID',
          message: 'conversationId must be a positive integer',
        },
      });
      return;
    }

    if (!ctx.accountId) {
      ctx.ackResult(false, sdkType, requestId, {
        error: {
          code: 'SDK_NOT_READY',
          message: 'No active account in dashboard context',
        },
      });
      return;
    }

    // Already on the requested conversation → no-op router push.
    // Only meaningful when called from a conversation-scoped surface;
    // full-screen apps never have a conversation_id route param so this
    // branch correctly falls through to navigation.
    if (ctx.currentRouteConversationId === String(conversationId)) {
      ctx.ackResult(true, sdkType, requestId, {
        result: {
          navigated: true,
          conversationId,
          route:
            ctx.router?.currentRoute?.value?.fullPath ||
            ctx.router?.currentRoute?.fullPath ||
            '',
          alreadyActive: true,
        },
      });
      return;
    }

    let conv = ctx.store.getters.getConversationById(conversationId);
    if (!conv) {
      try {
        const response = await ctx.conversationApiShow(conversationId);
        conv = response?.data;
      } catch (error) {
        const status = error?.response?.status;
        if (status === 403) {
          ctx.ackResult(false, sdkType, requestId, {
            error: {
              code: 'FORBIDDEN',
              message:
                'Agent is not a member of the inbox owning this conversation',
            },
          });
          return;
        }
        // 404 OR foreign account → uniform NOT_FOUND (don't leak boundary).
        ctx.ackResult(false, sdkType, requestId, {
          error: {
            code: 'CONVERSATION_NOT_FOUND',
            message: `No conversation with id ${conversationId}`,
          },
        });
        return;
      }
    }

    const path = ctx.buildConversationPath(
      ctx.accountId,
      inboxId || conv?.inbox_id,
      conversationId
    );

    try {
      if (target === 'blank') {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else {
        await ctx.router.push({ path });
      }
    } catch (error) {
      ctx.ackResult(false, sdkType, requestId, {
        error: {
          code: 'SDK_NOT_READY',
          message: error?.message || 'Navigation failed',
        },
      });
      return;
    }

    if (
      ctx.surface === 'full' &&
      closeFullScreen !== false &&
      ctx.onFrameClosed
    ) {
      ctx.onFrameClosed({ conversationId });
    }

    ctx.ackResult(true, sdkType, requestId, {
      result: {
        navigated: true,
        conversationId,
        route: path,
      },
    });
  },

  /**
   * Build context request message
   */
  buildContextRequestMessage(requestId, contextType, options = {}) {
    return {
      type: SDK_MESSAGE_TYPES.CONTEXT_REQUEST,
      data: {
        requestId,
        contextType,
        options,
        timestamp: Date.now(),
      },
    };
  },

  // ============================================
  // Inter-App Tool & Context Protocol Builders
  // ============================================

  /**
   * Build tool-invoke message (Container → target app)
   */
  buildToolInvokeMessage(requestId, callerRequestId, sourceApp, tool, params) {
    return {
      type: SDK_MESSAGE_TYPES.TOOL_INVOKE,
      data: {
        requestId,
        callerRequestId,
        sourceApp,
        tool,
        params,
        timestamp: Date.now(),
      },
    };
  },

  /**
   * Build tool-cancel message (Container → target app)
   */
  buildToolCancelMessage(requestId) {
    return {
      type: SDK_MESSAGE_TYPES.TOOL_CANCEL,
      data: { requestId, timestamp: Date.now() },
    };
  },

  /**
   * Build ctx-fetch message (Container → target app)
   */
  buildCtxFetchMessage(
    requestId,
    callerRequestId,
    sourceApp,
    contextName,
    options
  ) {
    return {
      type: SDK_MESSAGE_TYPES.CTX_FETCH,
      data: {
        requestId,
        callerRequestId,
        sourceApp,
        contextName,
        options,
        timestamp: Date.now(),
      },
    };
  },

  /**
   * Build catalog-updated message (Container → all apps)
   */
  buildCatalogUpdatedMessage(tools, contexts) {
    return {
      type: SDK_MESSAGE_TYPES.CATALOG_UPDATED,
      data: { tools, contexts, timestamp: Date.now() },
    };
  },

  /**
   * Build tool catalog response (Container → requesting app)
   */
  buildToolCatalogMessage(requestId, tools) {
    return {
      type: SDK_MESSAGE_TYPES.TOOL_CATALOG,
      data: { requestId, tools, timestamp: Date.now() },
    };
  },

  /**
   * Build context catalog response (Container → requesting app)
   */
  buildCtxCatalogMessage(requestId, contexts) {
    return {
      type: SDK_MESSAGE_TYPES.CTX_CATALOG,
      data: { requestId, contexts, timestamp: Date.now() },
    };
  },

  /**
   * Build event-partial message (Container → caller app, streaming)
   */
  buildEventPartialMessage(requestId, data) {
    return {
      type: SDK_MESSAGE_TYPES.EVENT_PARTIAL,
      data: { requestId, ...data, timestamp: Date.now() },
    };
  },

  /**
   * Build token-refreshed message (Container → SDK)
   */
  buildTokenRefreshedMessage(token, expiresIn) {
    return {
      type: SDK_MESSAGE_TYPES.TOKEN_REFRESHED,
      data: {
        token,
        expiresIn,
        timestamp: Date.now(),
      },
    };
  },

  /**
   * Check if event is SDK ready signal
   */
  isSDKReadyEvent(eventData) {
    return (
      eventData &&
      typeof eventData === 'object' &&
      eventData.type === SDK_MESSAGE_TYPES.SDK_READY
    );
  },

  /**
   * Check if event is SDK event
   */
  isSDKEvent(eventData) {
    return (
      eventData &&
      typeof eventData === 'object' &&
      eventData.type === SDK_MESSAGE_TYPES.SDK_EVENT
    );
  },

  /**
   * Check if event is legacy fetch-info
   */
  isLegacyFetchInfo(eventData) {
    return eventData === SDK_MESSAGE_TYPES.LEGACY_FETCH_INFO;
  },

  /**
   * Handle copy-to-composer by emitting bus events
   */
  emitCopyToComposer(content, attachments) {
    let hasContent = false;
    let attachmentCount = 0;

    if (content && content.trim()) {
      hasContent = true;
      // NOTICE: KAPTION-INTEGRATION — Only emit to normal editor;
      // ReplyBox.addIntoEditor routes to the correct editor (rich or plain)
      // based on showRichContentEditor. Emitting both caused double insertion.
      emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, content);
    }

    if (attachments && attachments.length > 0) {
      attachmentCount = attachments.length;
      emitter.emit(BUS_EVENTS.INSERT_ATTACHMENTS_INTO_COMPOSER, attachments);
    }

    return { hasContent, attachmentCount };
  },

  /**
   * Log message from dashboard app
   */
  logFromApp(level, message, data, appId = 'unknown') {
    const prefix = `[DashboardApp:${appId}]`;
    // eslint-disable-next-line no-console
    const logFn = console[level] || console.log;
    // eslint-disable-next-line no-console
    logFn(prefix, message, data || '');
  },
};

/**
 * Composable for handling Dashboard App SDK communication
 * Provides all SDK event handling, message sending, and context management
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.getIframeElements - Function that returns array of iframe elements
 * @param {Function} options.getContext - Function that returns current dashboard app context
 * @param {String} options.logPrefix - Prefix for console logs (e.g., '[Frame.vue]', '[KaptionCopilot]')
 * @param {String} options.surface - Surface type ('main' or 'sidebar')
 * @param {Object} options.sidebarParams - Additional sidebar parameters
 * @returns {Object} SDK utilities and handlers
 */
export function useDashboardAppSDK(options = {}) {
  const {
    getIframeElements = () => [],
    getContext = () => ({}),
    logPrefix = '[DashboardAppSDK]',
    surface = 'main',
    sidebarParams = {},
  } = options;

  const store = useStore();
  const { locale } = useI18n();

  // State
  const iframeLoading = ref(true);
  const pendingContextRequests = ref(new Map());

  // Get UI settings
  const uiSettings = computed(() => store.getters.getUISettings || {});
  const theme = computed(() => uiSettings.value?.theme || 'light');

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Generate a unique request ID for tracking responses
   */
  const generateRequestId = () => {
    return `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Check if a message event is from one of our managed iframes
   */
  const isFromOwnIframe = event => {
    const iframes = getIframeElements();
    return iframes.some(
      iframe => iframe && event.source === iframe.contentWindow
    );
  };

  /**
   * Decode base64 attachments to File objects
   */
  const decodeAttachments = attachments => {
    if (!attachments || attachments.length === 0) return [];

    return attachments.map(att => {
      const byteString = atob(att.data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: att.mimeType });
      return new File([blob], att.title || 'attachment', {
        type: att.mimeType,
      });
    });
  };

  // ============================================
  // OUTBOUND MESSAGES (Container → SDK)
  // ============================================

  /**
   * Send a message to a specific iframe
   */
  const postMessageToIframe = (iframe, message) => {
    if (!iframe || !iframe.contentWindow) return false;
    try {
      iframe.contentWindow.postMessage(JSON.stringify(message), '*');
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to post message:`, error);
      return false;
    }
  };

  /**
   * Send context to a specific iframe
   */
  const sendContextToIframe = (iframe, context) => {
    const message = {
      type: 'chatwoot-dashboard-context',
      data: {
        conversation: context.conversation,
        contact: context.contact,
        currentAgent: context.currentAgent,
        ui: {
          surface,
          sidebarParams,
        },
        timestamp: Date.now(),
      },
    };

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} Sending context:`, {
      conversationId: context.conversation?.id,
      contactId: context.contact?.id,
    });

    return postMessageToIframe(iframe, message);
  };

  /**
   * Send context to all managed iframes
   */
  const sendContextToAllIframes = context => {
    const iframes = getIframeElements();
    iframes.forEach(iframe => sendContextToIframe(iframe, context));
  };

  /**
   * Send user preferences (theme, language, timezone) to a specific iframe
   */
  const sendUserPreferences = iframe => {
    const message = {
      type: 'chatwoot-dashboard-settings-update',
      data: {
        userPreferences: {
          theme: theme.value,
          language: locale.value,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        ui: {
          surface,
          sidebarParams,
        },
      },
    };

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} Sending preferences:`, message);
    return postMessageToIframe(iframe, message);
  };

  /**
   * Send user preferences to all managed iframes
   */
  const sendUserPreferencesToAllIframes = () => {
    const iframes = getIframeElements();
    iframes.forEach(iframe => sendUserPreferences(iframe));
  };

  /**
   * Send UI configuration to a specific iframe
   */
  const sendUIConfig = (iframe, config = {}) => {
    const message = {
      type: 'chatwoot-dashboard-ui-config',
      data: {
        keyboard: config.keyboard || { enabled: true, type: 'text' },
        display: config.display || { mode: 'full', scrollable: true },
        capabilities: config.capabilities || {
          canSendMessages: true,
          canSendAttachments: true,
          canAccessContacts: true,
        },
        timestamp: Date.now(),
      },
    };

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} Sending UI config:`, message);
    return postMessageToIframe(iframe, message);
  };

  /**
   * Send UI config to all managed iframes
   */
  const sendUIConfigToAllIframes = config => {
    const iframes = getIframeElements();
    iframes.forEach(iframe => sendUIConfig(iframe, config));
  };

  /**
   * Send event result back to the SDK
   */
  const sendEventResult = (sourceWindow, requestId, success, sdkType, data) => {
    if (!sourceWindow) {
      // eslint-disable-next-line no-console
      console.warn(`${logPrefix} No source window to send result to`);
      return;
    }

    const message = {
      type: 'chatwoot-dashboard-event-result',
      data: {
        requestId,
        success,
        sdkType,
        ...data,
      },
    };

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} Sending event result:`, message);

    try {
      sourceWindow.postMessage(JSON.stringify(message), '*');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to send event result:`, error);
    }
  };

  /**
   * Request context data from dashboard app
   * @returns {Promise<Object>} Promise resolving to context response
   */
  const requestAppContext = (iframe, contextType, requestOptions = {}) => {
    return new Promise((resolve, reject) => {
      if (!iframe || !iframe.contentWindow) {
        reject(new Error('Invalid frame element'));
        return;
      }

      const requestId = generateRequestId();
      const timeoutMs = requestOptions.timeout || 30000;

      const message = {
        type: 'chatwoot-dashboard-context-request',
        data: {
          requestId,
          contextType,
          options: requestOptions,
          timestamp: Date.now(),
        },
      };

      // Store pending request
      let timeoutId;
      const handleResponse = event => {
        if (!event.data) return;

        let messageData = event.data;
        if (typeof event.data === 'string') {
          try {
            messageData = JSON.parse(event.data);
          } catch (e) {
            return;
          }
        }

        if (
          messageData.type === 'chatwoot-dashboard-app-sdk' &&
          messageData.data?.sdkType === 'context-response' &&
          messageData.data?.payload?.requestId === requestId
        ) {
          clearTimeout(timeoutId);
          window.removeEventListener('message', handleResponse);
          pendingContextRequests.value.delete(requestId);

          const { success, data, error } = messageData.data.payload;
          if (success) {
            resolve(data);
          } else {
            reject(new Error(error?.message || 'Context request failed'));
          }
        }
      };

      timeoutId = setTimeout(() => {
        window.removeEventListener('message', handleResponse);
        pendingContextRequests.value.delete(requestId);
        reject(new Error('Context request timeout'));
      }, timeoutMs);

      pendingContextRequests.value.set(requestId, {
        handleResponse,
        timeoutId,
      });
      window.addEventListener('message', handleResponse);

      // eslint-disable-next-line no-console
      console.log(`${logPrefix} Requesting app context:`, message);
      postMessageToIframe(iframe, message);
    });
  };

  // ============================================
  // INBOUND MESSAGE HANDLERS (SDK → Container)
  // ============================================

  /**
   * Handle send-message event from SDK
   */
  const handleSendMessage = async (payload, sourceWindow) => {
    const { requestId, content, messageType, conversationId, attachments } =
      payload || {};

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} handleSendMessage:`, {
      requestId,
      content,
      messageType,
      conversationId,
      hasAttachments: !!attachments?.length,
    });

    const hasAttachments = attachments && attachments.length > 0;
    if (!hasAttachments && (!content || typeof content !== 'string')) {
      sendEventResult(sourceWindow, requestId, false, 'send-message', {
        error: {
          code: 'INVALID_CONTENT',
          message: 'Message content is required',
        },
      });
      return;
    }

    // Get conversation ID from payload or current context
    const context = getContext();
    const targetConversationId = conversationId || context.conversation?.id;
    if (!targetConversationId) {
      sendEventResult(sourceWindow, requestId, false, 'send-message', {
        error: {
          code: 'NO_CONVERSATION',
          message: 'No conversation context available',
        },
      });
      return;
    }

    try {
      const files = decodeAttachments(attachments);

      const isPrivate = messageType === 'private';
      const messagePayload = {
        conversationId: targetConversationId,
        message: content || '',
        private: isPrivate,
      };

      if (files.length > 0) {
        messagePayload.files = files;
      }

      await store.dispatch('createPendingMessageAndSend', messagePayload);

      // eslint-disable-next-line no-console
      console.log(`${logPrefix} Message sent successfully`, {
        hasAttachments: files.length > 0,
      });

      sendEventResult(sourceWindow, requestId, true, 'send-message', {
        result: {
          sent: true,
          conversationId: targetConversationId,
          hasAttachments: files.length > 0,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to send message:`, error);

      sendEventResult(sourceWindow, requestId, false, 'send-message', {
        error: {
          code: 'SEND_FAILED',
          message: error.message || 'Failed to send message',
        },
      });
    }
  };

  /**
   * Handle copy-to-composer event from SDK
   */
  const handleCopyToComposer = (payload, sourceWindow) => {
    const { requestId, content, attachments } = payload || {};

    // eslint-disable-next-line no-console
    console.log(`${logPrefix} handleCopyToComposer:`, {
      requestId,
      content,
      hasAttachments: !!attachments?.length,
    });

    try {
      let hasContent = false;
      let attachmentCount = 0;

      // Inject text content into the composer editor
      // NOTICE: KAPTION-INTEGRATION — Only emit to normal editor;
      // ReplyBox.addIntoEditor routes to the correct editor (rich or plain)
      // based on showRichContentEditor. Emitting both caused double insertion.
      if (content && content.trim()) {
        hasContent = true;
        emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, content);
      }

      // Inject attachments into the composer
      if (attachments && attachments.length > 0) {
        attachmentCount = attachments.length;
        emitter.emit(BUS_EVENTS.INSERT_ATTACHMENTS_INTO_COMPOSER, attachments);
      }

      sendEventResult(sourceWindow, requestId, true, 'copy-to-composer', {
        result: {
          populated: true,
          hasContent,
          attachmentCount,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to copy to composer:`, error);

      sendEventResult(sourceWindow, requestId, false, 'copy-to-composer', {
        error: {
          code: 'COPY_FAILED',
          message: error.message || 'Failed to copy to composer',
        },
      });
    }
  };

  /**
   * Handle log event from SDK
   */
  const handleLog = (payload, appId = 'unknown') => {
    const { level, message, data } = payload || {};
    const prefix = `[DashboardApp:${appId}]`;
    // eslint-disable-next-line no-console
    const logFn = console[level] || console.log;
    // eslint-disable-next-line no-console
    logFn(prefix, message, data || '');
  };

  /**
   * Handle context-response event from SDK (response to requestAppContext)
   * Note: This is handled internally by requestAppContext's promise
   */
  const handleContextResponse = () => {
    // Context responses are handled by the pending request handlers
    // set up in requestAppContext. This is here for documentation.
  };

  // ============================================
  // MAIN MESSAGE HANDLER
  // ============================================

  /**
   * Main message event handler - routes SDK events to appropriate handlers
   * @param {MessageEvent} event - The message event
   * @param {Object} callbacks - Optional callbacks for specific events
   */
  const handleMessage = (event, callbacks = {}) => {
    // Only process messages from our own iframes
    if (!isFromOwnIframe(event)) {
      return;
    }

    // Handle legacy fetch-info message
    if (event.data === 'chatwoot-dashboard-app:fetch-info') {
      callbacks.onLegacyFetchInfo?.();
      return;
    }

    // Handle SDK ready message
    if (
      event.data &&
      typeof event.data === 'object' &&
      event.data.type === 'chatwoot-dashboard-app-sdk-ready'
    ) {
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} SDK ready signal received`);
      iframeLoading.value = false;

      // Send initial context and preferences
      const context = getContext();
      sendContextToAllIframes(context);
      sendUserPreferencesToAllIframes();

      callbacks.onSdkReady?.();
      return;
    }

    // Handle SDK events (bidirectional communication)
    if (
      event.data &&
      typeof event.data === 'object' &&
      event.data.type === 'chatwoot-dashboard-app-sdk'
    ) {
      const { sdkType, payload } = event.data.data || {};
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} SDK event received:`, { sdkType, payload });

      switch (sdkType) {
        case 'send-message':
          handleSendMessage(payload, event.source);
          break;
        case 'context-refresh-request':
          sendContextToAllIframes(getContext());
          break;
        case 'log':
          handleLog(payload, callbacks.getAppId?.() || 'unknown');
          break;
        case 'copy-to-composer':
          handleCopyToComposer(payload, event.source);
          break;
        case 'context-response':
          handleContextResponse(payload);
          break;
        case 'token-refresh':
          callbacks.onTokenRefresh?.(event.source);
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(`${logPrefix} Unknown SDK event type:`, sdkType);
      }
    }
  };

  /**
   * Create a bound message handler for use with addEventListener
   */
  const createMessageHandler = callbacks => {
    return event => handleMessage(event, callbacks);
  };

  // ============================================
  // LIFECYCLE HELPERS
  // ============================================

  /**
   * Set up message listener - call in mounted/onMounted
   */
  const setupMessageListener = callbacks => {
    const handler = createMessageHandler(callbacks);
    window.addEventListener('message', handler);
    return handler;
  };

  /**
   * Remove message listener - call in unmounted/onUnmounted
   */
  const removeMessageListener = handler => {
    window.removeEventListener('message', handler);
  };

  /**
   * Handle iframe load event
   */
  const onIframeLoad = () => {
    iframeLoading.value = false;
    // eslint-disable-next-line no-console
    console.log(`${logPrefix} Iframe loaded, waiting for SDK ready`);
    // Don't send context here - wait for SDK ready signal
  };

  /**
   * Send a refreshed token to the SDK iframe
   * @param {Window} sourceWindow - The iframe's contentWindow
   * @param {string} token - New JWT token
   * @param {number} expiresIn - Token TTL in seconds
   */
  const sendTokenRefreshed = (sourceWindow, token, expiresIn) => {
    if (!sourceWindow) return;

    const message = DashboardAppSDKUtils.buildTokenRefreshedMessage(
      token,
      expiresIn
    );

    try {
      sourceWindow.postMessage(JSON.stringify(message), '*');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${logPrefix} Failed to send refreshed token:`, error);
    }
  };

  return {
    // State
    iframeLoading,

    // Utility functions
    generateRequestId,
    isFromOwnIframe,
    decodeAttachments,

    // Outbound messages (Container → SDK)
    postMessageToIframe,
    sendContextToIframe,
    sendContextToAllIframes,
    sendUserPreferences,
    sendUserPreferencesToAllIframes,
    sendUIConfig,
    sendUIConfigToAllIframes,
    sendEventResult,
    requestAppContext,
    sendTokenRefreshed,

    // Inbound message handlers (SDK → Container)
    handleSendMessage,
    handleCopyToComposer,
    handleLog,
    handleMessage,
    createMessageHandler,

    // Lifecycle helpers
    setupMessageListener,
    removeMessageListener,
    onIframeLoad,
  };
}

export default useDashboardAppSDK;
